import { GENERAL_ID } from '../components/Stores/exposureDataStore';
import { IMaterialExposure, INetExposure } from '../sp-types/netExposureType';
import { convertDateToMonthYear } from './MonthYear/monthYear';

export const getGeneralExposure = (
  exposure: INetExposure,
  convertionMap: Record<string, number>,
): IMaterialExposure => {
  const initialAccumulator: IMaterialExposure = {
    financialPositions: 0,
    futureOrder: 0,
    inventorySum: 0,
    futureExpCalendar: [],
    positionExpCalendar: [],
  };

  return Object.keys(exposure.materialsExposure)
    .filter((key) => key !== GENERAL_ID)
    .reduce((acc, materialId) => {
      const currentObject = exposure.materialsExposure[materialId] || {
        financialPositions: 0,
        futureOrder: 0,
        inventorySum: 0,
      };

      acc.financialPositions += currentObject.financialPositions * convertionMap[materialId];
      acc.futureOrder += currentObject.futureOrder * convertionMap[materialId];
      acc.inventorySum += currentObject.inventorySum * convertionMap[materialId];

      return acc;
    }, initialAccumulator);
};

export const getExposureCalc = ({ inventorySum, financialPositions, futureOrder }: IMaterialExposure): number =>
  inventorySum + financialPositions - futureOrder;

export const getNetExposure = (
  exposure: INetExposure,
  convertionMap: Record<string, number>,
  formulaId: string,
  isTotal?: boolean,
): number => {
  const materialExposure = isTotal
    ? getGeneralExposure(exposure, convertionMap)
    : exposure.materialsExposure[formulaId];
  return materialExposure ? getExposureCalc(materialExposure) : 0;
};

export const adjustExposureByMaterialPrices = (
  netExposure: INetExposure,
  materialPriceMap: Record<string, number>,
): INetExposure => {
  const adjustedMaterialsExposure: Record<string, IMaterialExposure> = Object.entries(
    netExposure.materialsExposure,
  ).reduce((acc, [materialId, { positionExpCalendar, futureExpCalendar, inventorySum, ...exposure }]) => {
    const price = materialPriceMap[materialId] || 1;
    acc[materialId] = {
      inventorySum: inventorySum * price,
      inventoryCalendar: { value: inventorySum, date: convertDateToMonthYear(new Date()) },
      futureOrder: exposure.futureOrder * price,
      financialPositions: exposure.financialPositions * price,
      futureExpCalendar: futureExpCalendar || [], // we need the material exposure, no need for the price
      positionExpCalendar: positionExpCalendar || [],
    };
    return acc;
  }, {} as Record<string, IMaterialExposure>);

  return {
    ...netExposure,
    materialsExposure: adjustedMaterialsExposure,
  };
};

export const isSameMonth = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

export const calcExposure = (netExposure: INetExposure, formulaId: string): number => {
  const materialExp = netExposure?.materialsExposure[formulaId] || {
    futureOrder: 0,
    inventorySum: 0,
    financialPositions: 0,
  };
  return materialExp.futureOrder - materialExp.inventorySum - materialExp.financialPositions;
};

export interface IExposureHistory {
  key: string;
  accountId: string;
  formulaId: string;
  createDate: Date;
  financialPosition: number;
  inventory: number;
  futureOrders: number;
}
